/**
 * What: Decides whether a Study Builder visit can start and what it starts from, once the page's
 *   `initial-state` source has been read.
 * Transport / data-flow: Pure functions over source data; no network and no React.
 * Why: Only a saved direct study or an explicit creation intent may open the builder. Retired
 *   setup/draft/fork URLs fail with a named error instead of becoming a guessed new study.
 */
import { studyBuilderWireSnapshotSchema, type StudyBuilderSnapshot } from '@testfeed/shopify-contracts'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { MerchantFacingError } from '../../merchantErrors'
import type { StudyBuilderInitialState } from '../../pages/study-builder.page'

export type StudyBuilderStart = {
  authoring?: StudyBuilderInitialState['authoring']
  direct: true
  /** What the form opens with: the saved direct study, or defaults for an explicit creation intent. */
  snapshot: StudyBuilderSnapshot
}

type DirectBuilderRoute = {
  studyId?: string
  setupId?: string | null
  draftId?: string | null
  entry?: 'new'
  forkParentPacketId?: string
  forkChangeReason?: string
}

type BootstrapRead = {
  direct?: boolean
  authoring?: StudyBuilderInitialState['authoring']
  deletion?: StudyBuilderInitialState['deletion']
}

function retiredBuilderRoute(): MerchantFacingError {
  return new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.legacyRouteRetired, {
    code: 'STUDY_BUILDER_LEGACY_ROUTE_RETIRED',
    status: 410,
  })
}

/** Reject old setup/draft URLs and malformed combinations before any source can be guessed. */
export function assertDirectStudyBuilderRoute(route: DirectBuilderRoute) {
  const studyId = route.studyId?.trim() || null
  const draftId = route.draftId?.trim() || null
  const hasFork = Boolean(route.forkParentPacketId?.trim() || route.forkChangeReason?.trim())
  if (route.setupId?.trim() || hasFork) throw retiredBuilderRoute()
  if (studyId) {
    if (draftId || route.entry === 'new') throw retiredBuilderRoute()
    return { kind: 'study' as const, studyId }
  }
  if (route.entry === 'new' && draftId) return { kind: 'creation' as const, creationIntentId: draftId }
  throw retiredBuilderRoute()
}

/** Throws bootstrap failures with their real codes; the lifecycle names and reports them. */
export function assertStudyBuilderStartable(read: BootstrapRead, route: DirectBuilderRoute) {
  const identity = assertDirectStudyBuilderRoute(route)
  if (read.direct !== true)
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.directStudyLoadFailed, {
      code: 'STUDY_BUILDER_SOURCE_MISMATCH',
    })
  if (
    identity.kind === 'study' &&
    !read.deletion &&
    (!read.authoring || read.authoring.authoring.studyId !== identity.studyId)
  ) {
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.notFound, {
      code: 'STUDY_AUTHORING_NOT_FOUND',
      status: 404,
    })
  }
}

/** The form starts from the checked direct-study document, or defaults for a new creation intent. */
export function resolveStudyBuilderStart(state: StudyBuilderInitialState): StudyBuilderStart {
  if (state.deletion)
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.deletionInProgress, {
      code: 'STUDY_DELETION_IN_PROGRESS',
    })
  if (state.direct !== true)
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.directStudyLoadFailed, {
      code: 'STUDY_BUILDER_SOURCE_MISMATCH',
    })
  // A saved study opens on its stored draft; a new creation intent opens on the Worker-built starting draft. The app never
  // builds its own defaults: if neither is present the server failed to supply a starting form, so fail loud.
  if (!state.authoring && !state.startingDraft)
    throw new MerchantFacingError(shopifyComponentCopy.studyBuilderPage.directStudyLoadFailed, {
      code: 'STUDY_BUILDER_START_DRAFT_MISSING',
    })
  const snapshot = state.authoring
    ? studyBuilderWireSnapshotSchema.parse(state.authoring.authoring.draft)
    : studyBuilderWireSnapshotSchema.parse(state.startingDraft)
  return { authoring: state.authoring, direct: true, snapshot }
}

/** One visit = shop, org, connection, and what the page opens (study or creation intent). */
export function studyBuilderVisitKey(
  scope: { shopDomain: string; orgId: string; connectionId: string } | null,
  view: {
    studyId?: string
    setupId?: string
    draftId?: string
    forkParentPacketId?: string
    forkChangeReason?: string
  },
) {
  const where = scope ? `${scope.shopDomain}:${scope.orgId}:${scope.connectionId}` : 'no-scope'
  const identity = view.studyId ? `study:${view.studyId}` : `intent:${view.draftId ?? ''}`
  return ['study-builder', where, identity].join('|')
}
